/**
 * Table entity helpers (docs/plans/2d/2d-full-parity-plan.md
 * Faz 6.5): the real in-drawing TABLE object. Cells are a plain 2D string
 * matrix so the entity serializes through the ledger for free; geometry is
 * resolved on write (position + cell metrics) and rendered by EntityLayer.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { settingsStorage } from '../../utils/settingsPersistence';

export interface TableData {
    /** rows[ri][ci] cell text (header row first). */
    cells: string[][];
    /** insertion corner (world, Z-up XY). */
    position: [number, number, number];
    /** cell width/height in world units. */
    colWidth: number;
    rowHeight: number;
    /** first row drawn as a header band. */
    hasHeader: boolean;
    /** optional live data-link name backing these cells (DATALINK). */
    linkName?: string;
}

/** Parse one CSV/TSV block (RFC4180 quotes; tab when no comma present). */
export function parseCsv(text: string): string[][] {
    const rows: string[][] = [];
    let row: string[] = [];
    let field = '';
    let inQuotes = false;
    const delim = text.includes('\t') && !text.includes(',') ? '\t' : ',';
    const pushField = () => { row.push(field); field = ''; };
    const pushRow = () => { pushField(); rows.push(row); row = []; };
    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (inQuotes) {
            if (ch === '"') {
                if (text[i + 1] === '"') { field += '"'; i++; }
                else inQuotes = false;
            } else field += ch;
            continue;
        }
        if (ch === '"') inQuotes = true;
        else if (ch === delim) pushField();
        else if (ch === '\n') pushRow();
        else if (ch === '\r') { /* swallow */ }
        else field += ch;
    }
    if (field.length > 0 || row.length > 0) pushRow();
    return rows.filter((r) => r.some((c) => c.trim() !== ''));
}

/** Uniform column count (pad ragged rows). */
export function normalizeTableCells(cells: string[][]): string[][] {
    const width = cells.reduce((m, r) => Math.max(m, r.length), 0);
    return cells.map((r) => {
        const copy = [...r];
        while (copy.length < width) copy.push('');
        return copy;
    });
}

/**
 * Data links (DATALINK parity): named external datasets the app keeps
 * in-document; TABLE/REFRESH pull rows from here. Persisted per settings
 * slice (real binding, not a notify stub).
 */
interface DataLinkRecord { name: string; rows: string[][]; source: string; }
interface DataLinkState {
    links: Record<string, DataLinkRecord>;
    /** Register/replace a named link's rows from CSV text. */
    set: (name: string, csvText: string, source: string) => void;
    remove: (name: string) => void;
    get: (name: string) => DataLinkRecord | undefined;
    names: () => string[];
}
export const useDataLinkStore = create<DataLinkState>()(
    persist(
        (set, get) => ({
            links: {},
            set: (name, csvText, source) => set((s) => ({
                links: { ...s.links, [name]: { name, rows: parseCsv(csvText), source } },
            })),
            remove: (name) => set((s) => {
                const next = { ...s.links };
                delete next[name];
                return { links: next };
            }),
            get: (name) => get().links[name],
            names: () => Object.keys(get().links),
        }),
        { name: 'yap-datalinks', storage: settingsStorage, partialize: (s) => ({ links: s.links }) }
    )
);
